/**
 * TimePassageService — pure functions for computing the effects of
 * narrative time passing (hours/days/weeks) on a character.
 *
 * Integrates with RecoveryService for damage-state classification and
 * healing rates. Does NOT mutate state — returns a summary of changes
 * for the store to apply.
 */

import { classifyDamageState, DamageState } from './RecoveryService';

export interface TimeAdvanceResult {
  damageHealed: number;
  criticalHealed: number;
  fatigueRemoved: number;
  characteristicRecovered: number;
  brokenLimbWeeksHealed: number;
  summary: string[];
}

export interface TimeAdvanceInput {
  totalDamage: number;
  woundsMax: number;
  criticalDamageCount: number;
  toughnessBonus: number;
  currentFatigue: number;
  characteristicDamage: number; // total across all characteristics
  brokenLimbWeeksRemaining: number;
  isBedRest: boolean;
  isUnderMedicalCare: boolean;
}

/**
 * Compute the effects of advancing time by a given number of hours.
 */
export function computeTimeAdvance(hours: number, input: TimeAdvanceInput): TimeAdvanceResult {
  const result: TimeAdvanceResult = {
    damageHealed: 0,
    criticalHealed: 0,
    fatigueRemoved: 0,
    characteristicRecovered: 0,
    brokenLimbWeeksHealed: 0,
    summary: []
  };

  const days = Math.floor(hours / 24);
  const weeks = Math.floor(hours / 168);
  const state = classifyDamageState(
    input.totalDamage,
    input.toughnessBonus,
    input.criticalDamageCount,
    false,
    false
  );

  // --- Fatigue recovery: 1 per hour of rest, all after 8 consecutive hours ---
  if (input.currentFatigue > 0) {
    if (hours >= 8) {
      result.fatigueRemoved = input.currentFatigue;
      result.summary.push(`All fatigue removed (${input.currentFatigue} levels, 8+ hours rest)`);
    } else {
      result.fatigueRemoved = Math.min(hours, input.currentFatigue);
      if (result.fatigueRemoved > 0) {
        result.summary.push(`${result.fatigueRemoved} fatigue removed (1/hour)`);
      }
    }
  }

  // --- Characteristic damage recovery: 1 per hour ---
  if (input.characteristicDamage > 0) {
    result.characteristicRecovered = Math.min(hours, input.characteristicDamage);
    if (result.characteristicRecovered > 0) {
      result.summary.push(`${result.characteristicRecovered} characteristic damage recovered`);
    }
  }

  // --- Natural healing (damage) ---
  if (state === DamageState.LightlyDamaged && days > 0) {
    if (input.isBedRest) {
      // Bed rest: TB damage per full day
      result.damageHealed = days * input.toughnessBonus;
      result.summary.push(
        `${result.damageHealed} damage healed (bed rest, ${days}d × TB ${input.toughnessBonus})`
      );
    } else {
      // Normal: 1 damage per day
      result.damageHealed = days;
      result.summary.push(`${result.damageHealed} damage healed (lightly damaged, 1/day)`);
    }
  } else if (state === DamageState.HeavilyDamaged && weeks > 0) {
    if (input.isBedRest) {
      result.damageHealed = weeks * input.toughnessBonus;
      result.summary.push(
        `${result.damageHealed} damage healed (bed rest, ${weeks}w × TB ${input.toughnessBonus})`
      );
    } else {
      result.damageHealed = weeks;
      result.summary.push(`${result.damageHealed} damage healed (heavily damaged, 1/week)`);
    }
  } else if (state === DamageState.CriticallyDamaged && weeks > 0) {
    if (input.isUnderMedicalCare && input.isBedRest) {
      result.criticalHealed = weeks;
      result.summary.push(
        `${result.criticalHealed} critical damage healed (medical care + bed rest, 1/week)`
      );
    } else if (state === DamageState.CriticallyDamaged) {
      result.summary.push('Critical damage does not heal without bed rest + medical care');
    }
  }

  // Cap healing to actual damage
  result.damageHealed = Math.min(result.damageHealed, input.totalDamage);
  result.criticalHealed = Math.min(result.criticalHealed, input.criticalDamageCount);

  // --- Broken limb tracking ---
  if (input.brokenLimbWeeksRemaining > 0 && weeks > 0) {
    result.brokenLimbWeeksHealed = Math.min(weeks, input.brokenLimbWeeksRemaining);
    const remaining = input.brokenLimbWeeksRemaining - result.brokenLimbWeeksHealed;
    if (remaining <= 0) {
      result.summary.push('Broken limb fully healed!');
    } else {
      result.summary.push(
        `Broken limb: ${result.brokenLimbWeeksHealed}w healed, ${remaining}w remaining`
      );
    }
  }

  if (result.summary.length === 0) {
    result.summary.push('No time-dependent effects to apply');
  }

  return result;
}
